import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { applyQuillFontSizeStep, ensureQuillFontSizesRegistered } from '../../utils/quillFontSize';

ensureQuillFontSizesRegistered();

export interface ResearchNoteModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  isDarkMode: boolean;

  noteTitle: string;
  onChangeTitle: (value: string) => void;

  noteCategory: string;
  onChangeCategory: (value: string) => void;

  noteContent: string;
  onChangeContent: (value: string) => void;

  categories: readonly string[];

  canSubmit: boolean;
  onSubmit: () => Promise<void>;
}

const ResearchNoteModal: React.FC<ResearchNoteModalProps> = ({
  isOpen,
  onRequestClose,
  isDarkMode,
  noteTitle,
  onChangeTitle,
  noteCategory,
  onChangeCategory,
  noteContent,
  onChangeContent,
  categories,
  canSubmit,
  onSubmit,
}) => {
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement | null>(null);
  const detectTextDirection = (raw: string, fallback: 'rtl' | 'ltr' = 'ltr'): 'rtl' | 'ltr' => {
    const text = String(raw ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!text) return fallback;

    const isRTLChar = (ch: string) => /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/.test(ch);
    const isLTRChar = (ch: string) => /[A-Za-z]/.test(ch);

    for (const ch of text) {
      if (/\s/.test(ch)) continue;
      if (isRTLChar(ch)) return 'rtl';
      if (isLTRChar(ch)) return 'ltr';
    }

    return fallback;
  };

  const notePlaceholder = (() => {
    const label = (noteCategory ?? '').trim();
    return label.length > 0 ? `Here you can write about ${label}` : 'Here you can write about research desk';
  })();

  useEffect(() => {
    if (!isCategoryOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsCategoryOpen(false);
    };
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      const root = categoryDropdownRef.current;
      if (!target || !root) return;
      if (!root.contains(target)) setIsCategoryOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isCategoryOpen]);

  const titleDir = detectTextDirection(noteTitle, 'ltr');
  const quillRef = useRef<any>(null);

  // Memoize ReactQuill modules to prevent re-initialization on every render
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: '#note-toolbar',
        handlers: {
          undo: () => {
            quillRef.current?.getEditor?.()?.history.undo();
          },
          redo: () => {
            quillRef.current?.getEditor?.()?.history.redo();
          },
        },
      },
      history: {
        delay: 1000,
        maxStack: 50,
        userOnly: true,
      },
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      'bold',
      'italic',
      'underline',
      'strike',
      'size',
      'list',
      'bullet',
      'indent',
      'link',
      'image',
      'align',
      'direction',
      'color',
      'background',
    ],
    []
  );

  useEscapeKey(isOpen, onRequestClose);

  // Auto-detect RTL/LTR based on content
  useEffect(() => {
    if (!isOpen || !quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const editorElement = editor.root;

    const handleTextChange = () => {
      const text = editor.getText().trim();
      if (text.length === 0) {
        const dir: 'rtl' | 'ltr' = 'ltr';
        editorElement.style.direction = dir;
        editorElement.style.textAlign = 'left';

        try {
          editor.format('direction', dir, 'silent');
          editor.format('align', 'left', 'silent');
          const len = typeof editor.getLength === 'function' ? Math.max(editor.getLength(), 1) : 1;
          editor.formatLine(0, len, { direction: dir, align: 'left' }, 'silent');
        } catch {
          // Ignore formatting failures when editor is not ready.
        }
        return;
      }

      const firstChar = text.charAt(0);
      const isRTL = /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/.test(firstChar);

      const dir: 'rtl' | 'ltr' = isRTL ? 'rtl' : 'ltr';
      editorElement.style.direction = dir;
      editorElement.style.textAlign = dir === 'rtl' ? 'right' : 'left';

      try {
        editor.format('direction', dir, 'silent');
        editor.format('align', dir === 'rtl' ? 'right' : 'left', 'silent');
        const len = typeof editor.getLength === 'function' ? Math.max(editor.getLength(), 1) : 1;
        editor.formatLine(0, len, { direction: dir, align: dir === 'rtl' ? 'right' : 'left' }, 'silent');
      } catch {
        // Ignore formatting failures when editor is not ready.
      }
    };

    editor.on('text-change', handleTextChange);
    return () => {
      editor.off('text-change', handleTextChange);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const noteCategoryDropdownWrapStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  };

  const noteCategoryDropdownButtonStyle: React.CSSProperties = {
    padding: '0.5rem 2.25rem 0.5rem 1rem',
    borderRadius: '6px',
    border: `1px solid ${isDarkMode ? '#374151' : '#D9D9D9'}`,
    backgroundColor: isDarkMode ? '#1F2937' : '#F6F6F6',
    fontSize: '16px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 500,
    lineHeight: '1',
    letterSpacing: '0.01em',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    userSelect: 'none',
    outline: 'none',
    minWidth: 220,
  };

  const noteCategoryDropdownArrowStyle: React.CSSProperties = {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    width: 18,
    height: 18,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const noteCategoryDropdownMenuStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    border: `1px solid ${isDarkMode ? '#111827' : '#D9D9D9'}`,
    borderRadius: '8px',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    padding: '0.5rem 0.75rem',
    direction: 'ltr',
    textAlign: 'left',
    zIndex: 1000,
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
    boxSizing: 'border-box',
  };

  const noteCategoryDropdownItemStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.75rem',
    padding: '0.45rem 0.2rem',
    height: '44px',
    cursor: 'pointer',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    fontSize: '16px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 500,
    lineHeight: '1',
    letterSpacing: '0.01em',
    userSelect: 'none',
    textAlign: 'left',
  };

  const noteCategoryDropdownCheckboxStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    margin: 0,
    borderRadius: 4,
    border: isDarkMode ? '2px solid #4B5563' : '2px solid #D9D9D9',
    background: isDarkMode ? '#111827' : '#fff',
    appearance: 'none',
    outline: 'none',
    display: 'inline-block',
    verticalAlign: 'middle',
  };

  const renderNoteCategoryCheckbox = (checked: boolean, props: any = {}) => (
    <span
      style={{
        ...noteCategoryDropdownCheckboxStyle,
        border: isDarkMode
          ? checked
            ? '2px solid #3B82F6'
            : '2px solid #4B5563'
          : '2px solid #D9D9D9',
        background: isDarkMode
          ? checked
            ? '#3B82F6'
            : '#111827'
          : '#fff',
        position: 'relative',
      }}
      {...props}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 12 12" style={{ display: 'block' }}>
          <polyline
            points="3,7 5.2,9 9,4.5"
            style={{
              fill: 'none',
              stroke: isDarkMode ? '#111827' : '#2F58F2',
              strokeWidth: 2.2,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            }}
          />
        </svg>
      )}
    </span>
  );

  return (
    <div
      id="note-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
      }}
      onClick={onRequestClose}
    >
      {/* CSS Styles - copied from Summary modal */}
      <style>{`
        .note-modal-quill .quill {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .note-modal-quill .ql-toolbar {
          order: 2;
          border: none !important;
          padding: 8px 0 !important;
          display: flex !important;
          gap: 8px !important;
          flex-wrap: nowrap !important;
          align-items: center !important;
          flex-shrink: 0 !important;
          min-height: 40px !important;
          background: ${isDarkMode ? '#111827' : 'white'} !important;
          margin-top: 8px !important;
        }
        .note-modal-quill .ql-container {
          order: 1;
          flex: 1;
          background-color: ${isDarkMode ? '#111827' : '#ffffff'};
          border: none !important;
          overflow: hidden;
        }
        .note-modal-quill .ql-editor {
          background-color: ${isDarkMode ? '#111827' : '#ffffff'};
          border-radius: 6px;
          min-height: 100px;
          max-height: 150px;
          overflow-y: auto;
          direction: auto;
          text-align: start;
          font-size: 18px;
        }
        .note-modal-quill .ql-editor.ql-blank::before {
          background-color: ${isDarkMode ? '#111827' : '#ffffff'};
        }
        .note-modal-quill .ql-editor p {
          direction: auto;
          text-align: start;
        }
        .note-modal-quill .ql-toolbar .ql-formats {
          margin-right: 0 !important;
          display: inline-flex !important;
          gap: 10px !important;
        }
        #note-toolbar button,
        #note-toolbar .ql-picker {
          width: 20px !important;
          height: 20px !important;
          padding: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        #note-toolbar .ql-picker-label {
          padding: 0 !important;
          width: 24px !important;
          height: 24px !important;
        }
        #note-toolbar .ql-align .ql-picker-label {
          width: 32px !important;
          height: 32px !important;
          padding: 0 !important;
        }
        #note-toolbar .ql-align .ql-picker-label svg {
          display: none !important;
        }
        #note-toolbar .ql-align .ql-picker-label::before {
          content: '' !important;
          display: block !important;
          width: 28px !important;
          height: 28px !important;
          background-image: url('/images/align.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        #note-toolbar .ql-undo img,
        #note-toolbar .ql-redo img {
          width: 18px !important;
          height: 18px !important;
        }
        #note-toolbar button img {
          width: 18px !important;
          height: 18px !important;
        }
        #note-toolbar .ql-fontDecrease img,
        #note-toolbar .ql-fontIncrease img {
          width: 24px !important;
          height: 24px !important;
        }
        #note-toolbar .ql-picker-label::before {
          width: 16px !important;
          height: 16px !important;
        }
        .note-modal-quill .ql-toolbar .ql-picker-options {
          max-height: 200px !important;
          overflow-y: auto !important;
        }
        .note-modal-quill .ql-toolbar button {
          width: 20px !important;
          height: 20px !important;
          padding: 4px !important;
        }
        .note-modal-quill .ql-toolbar button svg {
          width: 18px;
          height: 18px;
        }
        #note-toolbar .ql-mention {
          font-size: 0 !important;
          padding: 0 !important;
        }
        #note-toolbar .ql-mention::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/mention.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        #note-toolbar .ql-emoji {
          font-size: 16px;
        }
        #note-toolbar .ql-color .ql-picker-label,
        #note-toolbar .ql-background .ql-picker-label {
          padding: 0 !important;
          width: 28px !important;
          height: 28px !important;
        }
        #note-toolbar .ql-color .ql-picker-label svg,
        #note-toolbar .ql-background .ql-picker-label svg {
          display: none !important;
        }
        #note-toolbar .ql-color .ql-picker-label::before {
          content: '' !important;
          display: block !important;
          width: 20px !important;
          height: 20px !important;
          background-image: url('/images/font-color.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          margin: 2px !important;
        }
        #note-toolbar .ql-background .ql-picker-label::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/edit-editor.svg?v=20260303') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          margin: 2px !important;
        }
        #note-toolbar .ql-link svg {
          display: none !important;
        }
        #note-toolbar .ql-link::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/insert_link.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        #note-toolbar .ql-list svg {
          display: none !important;
        }
        #note-toolbar .ql-list::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/list_check_line.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        #note-toolbar .ql-indent svg {
          display: none !important;
        }
        #note-toolbar .ql-indent[value="-1"]::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/list-indent-increase.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        #note-toolbar .ql-indent[value="+1"]::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/list-indent-decrease.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        #note-toolbar .ql-bold svg {
          display: none !important;
        }
        #note-toolbar .ql-bold::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/bold_line.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        #note-toolbar .ql-italic svg {
          display: none !important;
        }
        #note-toolbar .ql-italic::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/italic_line.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        #note-toolbar .ql-underline svg {
          display: none !important;
        }
        #note-toolbar .ql-underline::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/underline_line.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        #note-toolbar .ql-strike svg {
          display: none !important;
        }
        #note-toolbar .ql-strike::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/strikethrough_line.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
        .note-modal-quill .ql-tooltip {
          position: absolute !important;
          z-index: 10004 !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          bottom: 50px !important;
        }
        .note-modal-quill .ql-tooltip.ql-editing {
          left: 50% !important;
          transform: translateX(-50%) !important;
        }
        #note-toolbar .ql-emoji {
          font-size: 0 !important;
          padding: 0 !important;
        }
        #note-toolbar .ql-emoji::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/smile.svg') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          font-size: 16px !important;
        }
        #note-toolbar .ql-image svg {
          display: none !important;
        }
        #note-toolbar .ql-image::before {
          content: '' !important;
          display: block !important;
          width: 18px !important;
          height: 18px !important;
          background-image: url('/images/insertimage.svg?v=20260303') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
        }
      `}</style>

      <div
        id="add-note-modal"
        style={{
          backgroundColor: isDarkMode ? '#111827' : '#ffffff',
          border: `1px solid ${isDarkMode ? '#374151' : 'transparent'}`,
          borderRadius: '6px',
          width: '884px',
          height: '337px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          padding: '10px 12px',
          gap: '6px',
          opacity: 1,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          id="note-modal-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '0.5rem',
          }}
        >
          <h2
            style={{
              fontSize: '1rem',
              fontWeight: '600',
              margin: 0,
              color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
            }}
          >
            New Note
          </h2>
          <button
            onClick={onRequestClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6b7280',
              padding: 0,
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        <div
          id="note-content-wrapper"
          style={{
            border: `1px solid ${isDarkMode ? '#374151' : '#ECECF4'}`,
            borderRadius: '6px',
            padding: '12px',
            paddingBottom: '0px',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            gap: '6px',
            overflow: 'visible',
          }}
        >
          <div
            id="note-editor-content"
            style={{
              flex: 1,
              overflow: 'visible',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className="note-modal-quill" style={{ height: '200px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, minHeight: 0 }}>
                <ReactQuill
                  ref={quillRef}
                  value={noteContent}
                  onChange={onChangeContent}
                  placeholder={notePlaceholder}
                  style={{
                    height: '100%',
                    border: 'none',
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>

              <div
                id="note-toolbar"
                style={{
                  paddingTop: '18px',
                  paddingBottom: '12px',
                  display: 'flex',
                  gap: '0px',
                  flexWrap: 'nowrap',
                  background: isDarkMode ? '#111827' : 'white',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  borderBottom: '1px solid #ECECF4',
                  marginBottom: '2px',
                }}
              >
                <span className="ql-formats">
                  <button className="ql-undo" title="Undo">
                    <img src="/images/undo-icon.svg" alt="Undo" style={{ width: '18px', height: '18px' }} />
                  </button>
                  <button className="ql-redo" title="Redo">
                    <img
                      src="/images/undo-icon.svg"
                      alt="Redo"
                      style={{ width: '18px', height: '18px', transform: 'scaleX(-1)' }}
                    />
                  </button>
                </span>
                <span
                  style={{
                    display: 'inline-block',
                    width: '1px',
                    height: '20px',
                    backgroundColor: '#D0D0D0',
                    margin: '0 8px',
                    flexShrink: 0,
                  }}
                ></span>
                <span className="ql-formats">
                  <button className="ql-list" value="ordered" title="Ordered List"></button>
                  <button className="ql-list" value="bullet" title="Bullet List"></button>
                </span>
                <span className="ql-formats">
                  <select className="ql-align" title="Text Alignment"></select>
                </span>
                <span
                  style={{
                    display: 'inline-block',
                    width: '1px',
                    height: '20px',
                    backgroundColor: '#D0D0D0',
                    margin: '0 8px',
                    flexShrink: 0,
                  }}
                ></span>
                <span className="ql-formats">
                  <button className="ql-indent" value="+1" title="Indent Increase"></button>
                  <button className="ql-indent" value="-1" title="Indent Decrease"></button>
                </span>
                <span
                  style={{
                    display: 'inline-block',
                    width: '1px',
                    height: '20px',
                    backgroundColor: '#D0D0D0',
                    margin: '0 8px',
                    flexShrink: 0,
                  }}
                ></span>
                <span className="ql-formats">
                  <select className="ql-color" title="Text Color"></select>
                  <select className="ql-background" title="Background Color"></select>
                </span>
                <span className="ql-formats">
                  <button
                    className="ql-fontDecrease"
                    title="Decrease Font"
                    onClick={(e) => {
                      e.preventDefault();
                      const quill = quillRef.current?.getEditor?.();
                      applyQuillFontSizeStep(quill, -1);
                    }}
                    style={{ marginRight: '6px' }}
                  >
                    <img src="/images/a-arrow-down.svg" alt="A-" style={{ width: '24px', height: '24px' }} />
                  </button>
                  <button
                    className="ql-fontIncrease"
                    title="Increase Font"
                    onClick={(e) => {
                      e.preventDefault();
                      const quill = quillRef.current?.getEditor?.();
                      applyQuillFontSizeStep(quill, 1);
                    }}
                  >
                    <img src="/images/a-arrow-up.svg" alt="A+" style={{ width: '24px', height: '24px' }} />
                  </button>
                </span>
                <span
                  style={{
                    display: 'inline-block',
                    width: '1px',
                    height: '20px',
                    backgroundColor: '#D0D0D0',
                    margin: '0 8px',
                    flexShrink: 0,
                  }}
                ></span>
                <span className="ql-formats">
                  <button className="ql-bold" title="Bold"></button>
                  <button className="ql-italic" title="Italic"></button>
                  <button className="ql-underline" title="Underline"></button>
                  <button className="ql-strike" title="Strike"></button>
                </span>
                <span
                  style={{
                    display: 'inline-block',
                    width: '1px',
                    height: '20px',
                    backgroundColor: '#D0D0D0',
                    margin: '0 8px',
                    flexShrink: 0,
                  }}
                ></span>
                <span className="ql-formats">
                  <button className="ql-link" title="Insert Link"></button>
                  <button
                    className="ql-mention"
                    title="Mention (@)"
                    onClick={(e) => {
                      e.preventDefault();
                      const quill = document.querySelector('.note-modal-quill .ql-editor') as any;
                      if (quill) {
                        const selection = window.getSelection();
                        if (selection && selection.rangeCount > 0) {
                          const range = selection.getRangeAt(0);
                          const textNode = document.createTextNode('@');
                          range.insertNode(textNode);
                          range.setStartAfter(textNode);
                          range.setEndAfter(textNode);
                        }
                      }
                    }}
                  >
                    @
                  </button>
                  <button
                    className="ql-emoji"
                    title="Emoji"
                    onClick={(e) => {
                      e.preventDefault();
                      const quill = document.querySelector('.note-modal-quill .ql-editor') as any;
                      if (quill) {
                        const selection = window.getSelection();
                        if (selection && selection.rangeCount > 0) {
                          const range = selection.getRangeAt(0);
                          const textNode = document.createTextNode('😊');
                          range.insertNode(textNode);
                          range.setStartAfter(textNode);
                          range.setEndAfter(textNode);
                        }
                      }
                    }}
                  >
                    😊
                  </button>
                  <button className="ql-image" title="Insert Image"></button>
                </span>
              </div>
            </div>

            <div
              style={{
                width: '100%',
                height: '1px',
                backgroundColor: '#ECECF4',
                margin: '1px 0',
              }}
            ></div>
          </div>

          <div
            id="note-modal-footer"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '1rem',
              borderBottom: '1px solid #ECECF4',
              marginTop: '0rem',
              padding: '0.5rem',
              marginLeft: '-1rem',
              marginRight: '-1rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div ref={categoryDropdownRef} style={noteCategoryDropdownWrapStyle}>
                <button
                  type="button"
                  style={noteCategoryDropdownButtonStyle}
                  onClick={() => setIsCategoryOpen((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={isCategoryOpen}
                >
                  {noteCategory || 'Associated With:'}
                </button>
                <span aria-hidden="true" style={noteCategoryDropdownArrowStyle}>
                  <img src="/images/arrow.svg" alt="" style={{ width: 18, height: 18, display: 'block' }} />
                </span>
                {isCategoryOpen && (
                  <div role="listbox" aria-label="Associated With" style={noteCategoryDropdownMenuStyle}>
                    {categories.map((c, idx) => (
                      <div
                        key={c}
                        role="option"
                        aria-selected={noteCategory === c}
                        style={{
                          ...noteCategoryDropdownItemStyle,
                          borderTop: idx === 0 ? 'none' : `1px solid ${isDarkMode ? '#374151' : '#ECECF4'}`,
                        }}
                        onClick={() => {
                          onChangeCategory(c);
                          setIsCategoryOpen(false);
                        }}
                      >
                        {renderNoteCategoryCheckbox(noteCategory === c)}
                        {c}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="button"
              disabled={!canSubmit}
              onClick={onSubmit}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#000000',
                color: '#ffffff',
                border: '1px solid #ECECF4',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                opacity: canSubmit ? 1 : 0.6,
              }}
            >
              Add Note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchNoteModal;
